import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { immer } from 'zustand/middleware/immer';
import type {
  CreateMonitorRequest,
  FilePattern,
  ScanFrequency,
  MonitorType,
  MonitorStatus,
  PartitionMapping,
  LevelPartitionMapping,
} from '@/features/monitors/types';

/**
 * Monitor creation wizard steps
 */
export enum MonitorCreationStep {
  BASIC_INFO = 0,
  DIRECTORY_CONFIG = 1,
  PARTITION_MAPPING = 2,
  FILE_PATTERNS = 3,
  SCHEDULE = 4,
  REVIEW = 5,
}

/**
 * Monitor creation wizard state
 *
 * Manages multi-step monitor creation flow with basic info,
 * directory configuration, file patterns, and schedule settings.
 */
interface MonitorCreationState {
  // Current wizard state
  currentStep: MonitorCreationStep;
  isComplete: boolean;

  // Step 1: Basic Info
  name: string;
  description: string;
  dataset_id: string;

  // Step 2: Directory Config
  directory_path: string;
  directoryValidated: boolean;

  // Step 3: Partition Mapping (maps directory levels to dataset partitions)
  /** @deprecated Use level_partition_mappings instead */
  partition_mappings: PartitionMapping[];
  /** Level-based partition mappings (level 1 -> partition, level 2 -> partition, etc.) */
  level_partition_mappings: LevelPartitionMapping[];
  partitionMappingsValidated: boolean;

  // Step 4: File Patterns
  file_patterns: FilePattern;

  // Step 5: Schedule
  scan_frequency: ScanFrequency;
  monitor_type: MonitorType;
  auto_ingest: boolean;
  trigger_delay_seconds: number;
  batch_size: number;

  // Advanced Configuration
  status: MonitorStatus;
  default_schema_id?: string;
  config?: Record<string, unknown>;

  // Validation state
  errors: Record<string, string>;
}

/**
 * Monitor creation wizard actions
 */
interface MonitorCreationActions {
  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: MonitorCreationStep) => void;
  resetWizard: () => void;

  // Step 1: Basic Info
  setBasicInfo: (params: {
    name: string;
    description: string;
    dataset_id: string;
  }) => void;
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  setDatasetId: (dataset_id: string) => void;

  // Step 2: Directory Config
  setDirectoryPath: (path: string) => void;
  setDirectoryValidated: (validated: boolean) => void;

  // Step 3: Partition Mapping
  /** @deprecated Use level mapping actions instead */
  setPartitionMappings: (mappings: PartitionMapping[]) => void;
  /** @deprecated Use level mapping actions instead */
  addPartitionMapping: (mapping: PartitionMapping) => void;
  /** @deprecated Use level mapping actions instead */
  removePartitionMapping: (subdirectory: string) => void;
  /** @deprecated Use level mapping actions instead */
  updatePartitionMapping: (subdirectory: string, partition_name: string) => void;
  setPartitionMappingsValidated: (validated: boolean) => void;
  /** @deprecated Use clearLevelPartitionMappings instead */
  clearPartitionMappings: () => void;

  // Step 3: Level-based Partition Mapping
  setLevelPartitionMappings: (mappings: LevelPartitionMapping[]) => void;
  updateLevelPartitionMapping: (level: number, partition_name: string) => void;
  clearLevelPartitionMappings: () => void;

  // Step 4: File Patterns
  setFilePatterns: (patterns: FilePattern) => void;
  addIncludePattern: (pattern: string) => void;
  removeIncludePattern: (index: number) => void;
  addExcludePattern: (pattern: string) => void;
  removeExcludePattern: (index: number) => void;
  setRecursive: (recursive: boolean) => void;

  // Step 4: Schedule
  setSchedule: (params: {
    scan_frequency: ScanFrequency;
    monitor_type: MonitorType;
    auto_ingest: boolean;
  }) => void;
  setScanFrequency: (frequency: ScanFrequency) => void;
  setMonitorType: (type: MonitorType) => void;
  setAutoIngest: (autoIngest: boolean) => void;
  setTriggerDelay: (seconds: number) => void;
  setBatchSize: (size: number) => void;

  // Advanced Settings
  setStatus: (status: MonitorStatus) => void;
  setDefaultSchema: (schema_id?: string) => void;
  setConfig: (config: Record<string, unknown>) => void;

  // Validation
  setError: (field: string, error: string) => void;
  clearError: (field: string) => void;
  clearAllErrors: () => void;

  // Data retrieval
  getFormData: () => CreateMonitorRequest;

  // Completion
  markComplete: () => void;
}

type MonitorCreationStore = MonitorCreationState & MonitorCreationActions;

const TOTAL_STEPS = 6;

/**
 * Initial state for the monitor creation wizard
 */
const initialState: MonitorCreationState = {
  currentStep: MonitorCreationStep.BASIC_INFO,
  isComplete: false,

  // Basic Info
  name: '',
  description: '',
  dataset_id: '',

  // Directory Config
  directory_path: '',
  directoryValidated: false,

  // Partition Mapping
  partition_mappings: [],
  level_partition_mappings: [],
  partitionMappingsValidated: false,

  // File Patterns
  file_patterns: {
    include_patterns: ['*.parquet'],
    exclude_patterns: [],
    recursive: true,
  },

  // Schedule
  scan_frequency: 'daily',
  monitor_type: 'scheduled',
  auto_ingest: true,
  trigger_delay_seconds: 5,
  batch_size: 10,

  // Advanced
  status: 'active',
  default_schema_id: undefined,
  config: undefined,

  // Validation
  errors: {},
};

/**
 * Zustand store for monitor creation wizard
 *
 * Manages all state for the multi-step monitor creation process,
 * including basic info, directory configuration, file patterns, and scheduling.
 *
 * Uses Immer middleware for immutable state updates.
 *
 * @example
 * ```tsx
 * function MonitorCreationWizard() {
 *   const store = useMonitorCreationStore();
 *
 *   return (
 *     <div>
 *       {store.currentStep === MonitorCreationStep.BASIC_INFO && (
 *         <BasicInfoStep />
 *       )}
 *       {store.currentStep === MonitorCreationStep.DIRECTORY_CONFIG && (
 *         <DirectoryConfigStep />
 *       )}
 *       <WizardNavigation
 *         onNext={store.nextStep}
 *         onPrevious={store.previousStep}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export const useMonitorCreationStore = create<MonitorCreationStore>()(
  immer((set, get) => ({
    ...initialState,

    // Navigation actions
    nextStep: () =>
      set((state) => {
        if (state.currentStep < TOTAL_STEPS - 1) {
          state.currentStep += 1;
        }
      }),

    previousStep: () =>
      set((state) => {
        if (state.currentStep > 0) {
          state.currentStep -= 1;
        }
      }),

    goToStep: (step: MonitorCreationStep) =>
      set((state) => {
        state.currentStep = step;
      }),

    resetWizard: () =>
      set((state) => {
        Object.assign(state, initialState);
      }),

    // Step 1: Basic Info actions
    setBasicInfo: ({ name, description, dataset_id }) =>
      set((state) => {
        state.name = name;
        state.description = description;
        state.dataset_id = dataset_id;
      }),

    setName: (name: string) =>
      set((state) => {
        state.name = name;
      }),

    setDescription: (description: string) =>
      set((state) => {
        state.description = description;
      }),

    setDatasetId: (dataset_id: string) =>
      set((state) => {
        state.dataset_id = dataset_id;
      }),

    // Step 2: Directory Config actions
    setDirectoryPath: (path: string) =>
      set((state) => {
        state.directory_path = path;
        state.directoryValidated = false; // Reset validation when path changes
      }),

    setDirectoryValidated: (validated: boolean) =>
      set((state) => {
        state.directoryValidated = validated;
      }),

    // Step 3: Partition Mapping actions
    setPartitionMappings: (mappings: PartitionMapping[]) =>
      set((state) => {
        state.partition_mappings = mappings;
      }),

    addPartitionMapping: (mapping: PartitionMapping) =>
      set((state) => {
        // Avoid duplicates by subdirectory
        const exists = state.partition_mappings.some(
          (m) => m.subdirectory === mapping.subdirectory
        );
        if (!exists) {
          state.partition_mappings.push(mapping);
        }
      }),

    removePartitionMapping: (subdirectory: string) =>
      set((state) => {
        state.partition_mappings = state.partition_mappings.filter(
          (m) => m.subdirectory !== subdirectory
        );
      }),

    updatePartitionMapping: (subdirectory: string, partition_name: string) =>
      set((state) => {
        const mapping = state.partition_mappings.find(
          (m) => m.subdirectory === subdirectory
        );
        if (mapping) {
          mapping.partition_name = partition_name;
        }
      }),

    setPartitionMappingsValidated: (validated: boolean) =>
      set((state) => {
        state.partitionMappingsValidated = validated;
      }),

    clearPartitionMappings: () =>
      set((state) => {
        state.partition_mappings = [];
        state.partitionMappingsValidated = false;
      }),

    // Step 3: Level-based Partition Mapping actions
    setLevelPartitionMappings: (mappings: LevelPartitionMapping[]) =>
      set((state) => {
        state.level_partition_mappings = mappings;
      }),

    updateLevelPartitionMapping: (level: number, partition_name: string) =>
      set((state) => {
        const mapping = state.level_partition_mappings.find((m) => m.level === level);
        if (mapping) {
          mapping.partition_name = partition_name;
        }
      }),

    clearLevelPartitionMappings: () =>
      set((state) => {
        state.level_partition_mappings = [];
        state.partitionMappingsValidated = false;
      }),

    // Step 4: File Patterns actions
    setFilePatterns: (patterns: FilePattern) =>
      set((state) => {
        state.file_patterns = patterns;
      }),

    addIncludePattern: (pattern: string) =>
      set((state) => {
        if (!state.file_patterns.include_patterns.includes(pattern)) {
          state.file_patterns.include_patterns.push(pattern);
        }
      }),

    removeIncludePattern: (index: number) =>
      set((state) => {
        if (
          index >= 0 &&
          index < state.file_patterns.include_patterns.length
        ) {
          state.file_patterns.include_patterns.splice(index, 1);
        }
      }),

    addExcludePattern: (pattern: string) =>
      set((state) => {
        if (!state.file_patterns.exclude_patterns.includes(pattern)) {
          state.file_patterns.exclude_patterns.push(pattern);
        }
      }),

    removeExcludePattern: (index: number) =>
      set((state) => {
        if (
          index >= 0 &&
          index < state.file_patterns.exclude_patterns.length
        ) {
          state.file_patterns.exclude_patterns.splice(index, 1);
        }
      }),

    setRecursive: (recursive: boolean) =>
      set((state) => {
        state.file_patterns.recursive = recursive;
      }),

    // Step 5: Schedule actions
    setSchedule: ({ scan_frequency, monitor_type, auto_ingest }) =>
      set((state) => {
        state.scan_frequency = scan_frequency;
        state.monitor_type = monitor_type;
        state.auto_ingest = auto_ingest;
      }),

    setScanFrequency: (frequency: ScanFrequency) =>
      set((state) => {
        state.scan_frequency = frequency;
      }),

    setMonitorType: (type: MonitorType) =>
      set((state) => {
        state.monitor_type = type;
      }),

    setAutoIngest: (autoIngest: boolean) =>
      set((state) => {
        state.auto_ingest = autoIngest;
      }),

    setTriggerDelay: (seconds: number) =>
      set((state) => {
        state.trigger_delay_seconds = seconds;
      }),

    setBatchSize: (size: number) =>
      set((state) => {
        state.batch_size = size;
      }),

    // Advanced Settings actions
    setStatus: (status: MonitorStatus) =>
      set((state) => {
        state.status = status;
      }),

    setDefaultSchema: (schema_id?: string) =>
      set((state) => {
        state.default_schema_id = schema_id;
      }),

    setConfig: (config: Record<string, unknown>) =>
      set((state) => {
        state.config = config;
      }),

    // Validation actions
    setError: (field: string, error: string) =>
      set((state) => {
        state.errors[field] = error;
      }),

    clearError: (field: string) =>
      set((state) => {
        delete state.errors[field];
      }),

    clearAllErrors: () =>
      set((state) => {
        state.errors = {};
      }),

    // Data retrieval
    getFormData: () => {
      const state = get();

      const formData: CreateMonitorRequest = {
        name: state.name,
        directory_path: state.directory_path,
        dataset_id: state.dataset_id,
        description: state.description || undefined,
        file_patterns: state.file_patterns,
        scan_frequency: state.scan_frequency,
        monitor_type: state.monitor_type,
        status: state.status,
        auto_ingest: state.auto_ingest,
        trigger_delay_seconds: state.trigger_delay_seconds,
        batch_size: state.batch_size,
      };

      // Add optional fields only if they have values
      if (state.default_schema_id) {
        formData.default_schema_id = state.default_schema_id;
      }

      if (state.config && Object.keys(state.config).length > 0) {
        formData.config = state.config;
      }

      // Add partition mappings if configured (using level-based mappings)
      // Convert level mappings to the format expected by the backend
      if (state.level_partition_mappings && state.level_partition_mappings.length > 0) {
        // Store level mappings in config for backend processing
        formData.config = {
          ...formData.config,
          level_partition_mappings: state.level_partition_mappings,
        };
      }

      // Legacy: Also include old-style mappings if present (for backward compatibility)
      if (state.partition_mappings && state.partition_mappings.length > 0) {
        formData.partition_mappings = state.partition_mappings;
      }

      return formData;
    },

    // Completion
    markComplete: () =>
      set((state) => {
        state.isComplete = true;
      }),
  }))
);

/**
 * Selector hooks for optimized re-renders
 */

/**
 * Get current wizard step
 */
export const useCurrentStep = () =>
  useMonitorCreationStore((state) => state.currentStep);

/**
 * Get navigation actions and state
 */
export const useWizardNavigation = () =>
  useMonitorCreationStore((state) => ({
    currentStep: state.currentStep,
    nextStep: state.nextStep,
    previousStep: state.previousStep,
    goToStep: state.goToStep,
    isComplete: state.isComplete,
  }));

/**
 * Get basic info state and actions (Step 1)
 */
export const useBasicInfo = () =>
  useMonitorCreationStore((state) => ({
    name: state.name,
    description: state.description,
    dataset_id: state.dataset_id,
    setName: state.setName,
    setDescription: state.setDescription,
    setDatasetId: state.setDatasetId,
    setBasicInfo: state.setBasicInfo,
  }));

/**
 * Get directory configuration state and actions (Step 2)
 */
export const useDirectoryConfig = () =>
  useMonitorCreationStore((state) => ({
    directory_path: state.directory_path,
    directoryValidated: state.directoryValidated,
    setDirectoryPath: state.setDirectoryPath,
    setDirectoryValidated: state.setDirectoryValidated,
  }));

/**
 * Get partition mapping state and actions (Step 3)
 */
export const usePartitionMapping = () =>
  useMonitorCreationStore((state) => ({
    partition_mappings: state.partition_mappings,
    level_partition_mappings: state.level_partition_mappings,
    partitionMappingsValidated: state.partitionMappingsValidated,
    setPartitionMappings: state.setPartitionMappings,
    addPartitionMapping: state.addPartitionMapping,
    removePartitionMapping: state.removePartitionMapping,
    updatePartitionMapping: state.updatePartitionMapping,
    setPartitionMappingsValidated: state.setPartitionMappingsValidated,
    clearPartitionMappings: state.clearPartitionMappings,
    // Level-based mapping actions
    setLevelPartitionMappings: state.setLevelPartitionMappings,
    updateLevelPartitionMapping: state.updateLevelPartitionMapping,
    clearLevelPartitionMappings: state.clearLevelPartitionMappings,
  }));

/**
 * Get file patterns state and actions (Step 4)
 */
export const useFilePatterns = () =>
  useMonitorCreationStore((state) => ({
    file_patterns: state.file_patterns,
    setFilePatterns: state.setFilePatterns,
    addIncludePattern: state.addIncludePattern,
    removeIncludePattern: state.removeIncludePattern,
    addExcludePattern: state.addExcludePattern,
    removeExcludePattern: state.removeExcludePattern,
    setRecursive: state.setRecursive,
  }));

/**
 * Get schedule configuration state and actions (Step 5)
 */
export const useScheduleConfig = () =>
  useMonitorCreationStore((state) => ({
    scan_frequency: state.scan_frequency,
    monitor_type: state.monitor_type,
    auto_ingest: state.auto_ingest,
    trigger_delay_seconds: state.trigger_delay_seconds,
    batch_size: state.batch_size,
    setScanFrequency: state.setScanFrequency,
    setMonitorType: state.setMonitorType,
    setAutoIngest: state.setAutoIngest,
    setTriggerDelay: state.setTriggerDelay,
    setBatchSize: state.setBatchSize,
    setSchedule: state.setSchedule,
  }));

/**
 * Get advanced settings state and actions
 */
export const useAdvancedSettings = () =>
  useMonitorCreationStore(
    useShallow((state) => ({
      status: state.status,
      default_schema_id: state.default_schema_id,
      config: state.config,
      setStatus: state.setStatus,
      setDefaultSchema: state.setDefaultSchema,
      setConfig: state.setConfig,
    })),
  );

/**
 * Get validation state
 */
export const useValidationErrors = () =>
  useMonitorCreationStore(
    useShallow((state) => ({
      errors: state.errors,
      setError: state.setError,
      clearError: state.clearError,
      clearAllErrors: state.clearAllErrors,
      hasErrors: Object.keys(state.errors).length > 0,
    })),
  );

/**
 * Get complete monitor creation data for submission
 */
export const useMonitorCreationData = () =>
  useMonitorCreationStore(useShallow((state) => state.getFormData()));

/**
 * Get full wizard state for review step
 */
export const useMonitorCreationReview = () =>
  useMonitorCreationStore(
    useShallow((state) => ({
      name: state.name,
      description: state.description,
      dataset_id: state.dataset_id,
      directory_path: state.directory_path,
      partition_mappings: state.partition_mappings,
      level_partition_mappings: state.level_partition_mappings,
      file_patterns: state.file_patterns,
      scan_frequency: state.scan_frequency,
      monitor_type: state.monitor_type,
      auto_ingest: state.auto_ingest,
      trigger_delay_seconds: state.trigger_delay_seconds,
      batch_size: state.batch_size,
      status: state.status,
      default_schema_id: state.default_schema_id,
      config: state.config,
    })),
  );

/**
 * Validation helpers
 */

/**
 * Validate basic info step
 * Name validation must match backend: alphanumeric, underscores, and hyphens only
 */
export const validateBasicInfo = (
  name: string,
  dataset_id: string
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!name || name.trim().length === 0) {
    errors.name = 'Monitor name is required';
  } else if (name.length < 3) {
    errors.name = 'Monitor name must be at least 3 characters';
  } else if (name.length > 100) {
    errors.name = 'Monitor name must not exceed 100 characters';
  } else if (!/^[a-zA-Z0-9_-]+$/.test(name)) { // nosemgrep: rules.lgpl.nodejs_scan.javascript-dos-rule-regex_dos  -- anchored linear name regex over <=100-char input
    errors.name = 'Monitor name can only contain letters, numbers, underscores, and hyphens';
  }

  if (!dataset_id || dataset_id.trim().length === 0) {
    errors.dataset_id = 'Target dataset is required';
  }

  return errors;
};

/**
 * Validate directory path step
 */
export const validateDirectoryPath = (
  path: string,
  validated: boolean
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!path || path.trim().length === 0) {
    errors.directory_path = 'Directory path is required';
  } else if (!validated) {
    errors.directory_path = 'Directory path must be validated before continuing';
  }

  return errors;
};

/**
 * Validate partition mappings step (legacy - subdirectory-based)
 * Requires exactly one subdirectory mapped to each partition in the dataset
 * @deprecated Use validateLevelPartitionMappings instead
 */
export const validatePartitionMappings = (
  mappings: PartitionMapping[],
  datasetPartitions: string[]
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Check if all dataset partitions have a mapping
  const mappedPartitions = new Set(mappings.map((m) => m.partition_name));

  for (const partition of datasetPartitions) {
    if (!mappedPartitions.has(partition)) {
      errors[`partition_${partition}`] = `Partition "${partition}" requires a subdirectory mapping`;
    }
  }

  // Check for unmapped subdirectories (warning, not error)
  const usedSubdirectories = mappings.filter((m) => m.partition_name);
  if (usedSubdirectories.length !== datasetPartitions.length) {
    errors.partition_count = `Expected ${datasetPartitions.length} partition mapping(s), but found ${usedSubdirectories.length}`;
  }

  // Check for duplicate partition assignments
  const partitionCounts = new Map<string, number>();
  for (const mapping of mappings) {
    if (mapping.partition_name) {
      const count = partitionCounts.get(mapping.partition_name) || 0;
      partitionCounts.set(mapping.partition_name, count + 1);
    }
  }

  for (const [partition, count] of partitionCounts) {
    if (count > 1) {
      errors[`duplicate_${partition}`] = `Partition "${partition}" is mapped to multiple subdirectories`;
    }
  }

  return errors;
};

/**
 * Validate level-based partition mappings
 * Requires each directory level to map to exactly one partition column
 */
export const validateLevelPartitionMappings = (
  mappings: LevelPartitionMapping[],
  datasetPartitions: string[],
  _maxLevel: number
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // If no partitions in dataset, no mappings needed
  if (datasetPartitions.length === 0) {
    return errors;
  }

  // Check if we have mappings for all partitions
  const mappedPartitions = new Set(
    mappings.filter((m) => m.partition_name).map((m) => m.partition_name)
  );

  for (const partition of datasetPartitions) {
    if (!mappedPartitions.has(partition)) {
      errors[`partition_${partition}`] = `Partition "${partition}" is not mapped to any directory level`;
    }
  }

  // Check for duplicate partition assignments (same partition on multiple levels)
  const partitionCounts = new Map<string, number>();
  for (const mapping of mappings) {
    if (mapping.partition_name) {
      const count = partitionCounts.get(mapping.partition_name) || 0;
      partitionCounts.set(mapping.partition_name, count + 1);
    }
  }

  for (const [partition, count] of partitionCounts) {
    if (count > 1) {
      errors[`duplicate_${partition}`] = `Partition "${partition}" is mapped to multiple directory levels`;
    }
  }

  // Check that we don't have more levels mapped than partitions available
  const mappedLevels = mappings.filter((m) => m.partition_name).length;
  if (mappedLevels > datasetPartitions.length) {
    errors.level_count = `Mapped ${mappedLevels} levels but dataset only has ${datasetPartitions.length} partition(s)`;
  }

  return errors;
};

/**
 * Validate file patterns step
 */
export const validateFilePatterns = (
  patterns: FilePattern
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (
    !patterns.include_patterns ||
    patterns.include_patterns.length === 0
  ) {
    errors.include_patterns = 'At least one include pattern is required';
  }

  // Validate pattern syntax (basic check)
  patterns.include_patterns.forEach((pattern, index) => {
    if (!pattern || pattern.trim().length === 0) {
      errors[`include_pattern_${index}`] = 'Pattern cannot be empty';
    }
  });

  patterns.exclude_patterns.forEach((pattern, index) => {
    if (!pattern || pattern.trim().length === 0) {
      errors[`exclude_pattern_${index}`] = 'Pattern cannot be empty';
    }
  });

  return errors;
};

/**
 * Validate schedule configuration step
 */
export const validateSchedule = (
  trigger_delay_seconds: number,
  batch_size: number
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (trigger_delay_seconds < 0) {
    errors.trigger_delay_seconds = 'Trigger delay must be non-negative';
  } else if (trigger_delay_seconds > 3600) {
    errors.trigger_delay_seconds =
      'Trigger delay must not exceed 3600 seconds (1 hour)';
  }

  if (batch_size < 1) {
    errors.batch_size = 'Batch size must be at least 1';
  } else if (batch_size > 1000) {
    errors.batch_size = 'Batch size must not exceed 1000';
  }

  return errors;
};

/**
 * Validate all wizard steps
 */
export const validateAllSteps = (): {
  isValid: boolean;
  errors: Record<string, string>;
} => {
  const state = useMonitorCreationStore.getState();
  const allErrors: Record<string, string> = {};

  // Validate each step
  const basicInfoErrors = validateBasicInfo(state.name, state.dataset_id);
  const directoryErrors = validateDirectoryPath(
    state.directory_path,
    state.directoryValidated
  );
  const patternErrors = validateFilePatterns(state.file_patterns);
  const scheduleErrors = validateSchedule(
    state.trigger_delay_seconds,
    state.batch_size
  );

  // Merge all errors
  Object.assign(
    allErrors,
    basicInfoErrors,
    directoryErrors,
    patternErrors,
    scheduleErrors
  );

  return {
    isValid: Object.keys(allErrors).length === 0,
    errors: allErrors,
  };
};
